/**
 * The layers panel.
 *
 * Topmost first, which is the opposite of the document's own order and the way
 * every designer expects to read a stack. The reversal happens here, where it
 * is visible, rather than in the store.
 *
 * Reordering works two ways on purpose: dragging, because that is what a mouse
 * wants, and the bracket keys, because dragging a list is one of the least
 * accessible interactions there is. §6.6 is not a phase 10 item.
 */

import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import { moveLayerTo, renameLayer, toggleHidden, toggleLocked } from '../state/actions';
import type { Layer } from '../model/types';
import {
  IconBarcode,
  IconEye,
  IconEyeOff,
  IconField,
  IconGroup,
  IconImage,
  IconLock,
  IconPencil,
  IconQr,
  IconShape,
  IconText,
  IconUnlock,
  IconVector,
} from './icons';

export function LayersPanel({
  onClose,
  onMenu,
}: {
  onClose: () => void;
  onMenu?: (at: { x: number; y: number }) => void;
}) {
  const page = useEditor((s) => s.doc?.pages[s.pageIndex] ?? null);
  const selection = useEditor((s) => s.selection);
  const select = useEditor((s) => s.select);
  const toggleSelect = useEditor((s) => s.toggleSelect);
  const renaming = useEditor((s) => s.renaming);
  const beginRenaming = useEditor((s) => s.beginRenaming);
  const endRenaming = useEditor((s) => s.endRenaming);

  const [dragging, setDragging] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);

  if (!page) return null;

  // Topmost first.
  const rows = page.layers.slice().reverse();

  return (
    <aside className="tb-side" aria-label="Layers">
      <div className="tb-side__head">
        <h2 className="tb-side__title">Layers</h2>
        <button type="button" className="tb-btn tb-btn--quiet" onClick={onClose}>
          Close
        </button>
      </div>

      {rows.length === 0 && (
        <p className="tb-side__empty">
          Nothing on this page yet.
          <br />
          Add some text or a shape from the left.
        </p>
      )}

      <ul className="tb-layers" role="listbox" aria-multiselectable="true">
        {rows.map((layer) => (
          <li
            key={layer.id}
            className={
              'tb-layers__row' +
              (selection.includes(layer.id) ? ' tb-layers__row--on' : '') +
              (over === layer.id ? ' tb-layers__row--over' : '') +
              (layer.hidden ? ' tb-layers__row--hidden' : '')
            }
            // The same menu the canvas offers, from the list: a customer
            // reordering layers here should not have to go back to the card to
            // group two of them.
            onContextMenu={(event) => {
              event.preventDefault();

              const store = useEditor.getState();

              if (!store.selection.includes(layer.id)) store.select([layer.id]);

              onMenu?.({ x: event.clientX, y: event.clientY });
            }}
            // A row being renamed is not a row being dragged: the pointer
            // belongs to the caret while the name is being typed.
            draggable={renaming !== layer.id}
            onDragStart={() => setDragging(layer.id)}
            onDragEnd={() => {
              setDragging(null);
              setOver(null);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (dragging && dragging !== layer.id) setOver(layer.id);
            }}
            onDragLeave={() => setOver((current) => (current === layer.id ? null : current))}
            onDrop={(event) => {
              event.preventDefault();

              if (!dragging || dragging === layer.id) return;

              // The list runs top-down and the document runs bottom-up, so a
              // drop onto row N goes to the document index the row occupies.
              moveLayerTo(dragging, page.layers.findIndex((other) => other.id === layer.id));
              setDragging(null);
              setOver(null);
            }}
          >
            {renaming === layer.id ? (
              <NameField layer={layer} onDone={endRenaming} />
            ) : (
              <button
                type="button"
                className="tb-layers__pick"
                role="option"
                aria-selected={selection.includes(layer.id)}
                onClick={(event) => (event.shiftKey ? toggleSelect(layer.id) : select([layer.id]))}
                onDoubleClick={() => beginRenaming(layer.id)}
              >
                <Kind layer={layer} />
                <span className="tb-layers__name">{layer.name || describe(layer)}</span>
              </button>
            )}

            {/* Not a flag: the other two are toggles that say what a layer is,
                and this one does something. Its own class, so a row's flags
                stay the two that can be on or off. */}
            <button
              type="button"
              className="tb-layers__act"
              title="Rename"
              aria-label={`Rename ${layer.name || describe(layer)}`}
              onClick={() => beginRenaming(layer.id)}
            >
              <IconPencil size={15} />
            </button>

            <button
              type="button"
              className="tb-layers__flag"
              aria-pressed={layer.hidden}
              title={layer.hidden ? 'Show' : 'Hide'}
              aria-label={layer.hidden ? `Show ${layer.name}` : `Hide ${layer.name}`}
              onClick={() => toggleHidden(layer.id)}
            >
              {layer.hidden ? <IconEyeOff size={15} /> : <IconEye size={15} />}
            </button>

            <button
              type="button"
              className="tb-layers__flag"
              aria-pressed={layer.locked}
              title={layer.locked ? 'Unlock' : 'Lock'}
              aria-label={layer.locked ? `Unlock ${layer.name}` : `Lock ${layer.name}`}
              onClick={() => toggleLocked(layer.id)}
            >
              {layer.locked ? <IconLock size={15} /> : <IconUnlock size={15} />}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

/**
 * What kind of thing this row is.
 *
 * The same drawn set as the rail, at 15 px. Mixing typographic glyphs in here -
 * an "Aa" beside a "◆" beside a "|||" - reads as three different designers
 * having a go at the same list.
 */
function Kind({ layer }: { layer: Layer }) {
  const icons: Record<Layer['type'], JSX.Element> = {
    text: <IconText size={15} />,
    image: <IconImage size={15} />,
    shape: <IconShape size={15} />,
    svg: <IconVector size={15} />,
    graphic: <IconVector size={15} />,
    qr: <IconQr size={15} />,
    barcode: <IconBarcode size={15} />,
    group: <IconGroup size={15} />,
    placeholder: <IconField size={15} />,
  };

  return (
    <span className="tb-layers__kind" aria-hidden="true">
      {icons[layer.type]}
    </span>
  );
}

/**
 * The layer's name, being typed.
 *
 * A name is how a customer finds something again on a busy card, and it is what
 * the snap guides say - "aligned to Logo" is only useful if the logo is called
 * Logo. Started from the pencil, from a double-click on the name, or from the
 * Rename item in the layer menu, which is the place people look.
 *
 * The field is a sibling of the row's button rather than inside it: an input
 * within a button cannot be typed in, which is exactly how renaming came to be
 * a feature that existed and did not work.
 */
function NameField({ layer, onDone }: { layer: Layer; onDone: () => void }) {
  const [draft, setDraft] = useState(layer.name || describe(layer));
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);

  const finish = () => {
    const wanted = draft.trim();

    if (wanted && wanted !== layer.name) renameLayer(layer.id, wanted);

    onDone();
  };

  return (
    <input
      ref={field}
      className="tb-layers__rename"
      aria-label="Layer name"
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={finish}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        // The editor's own shortcuts are listening: Delete would delete the
        // layer being named, and the brackets would reorder it.
        event.stopPropagation();

        if (event.key === 'Enter') finish();

        if (event.key === 'Escape') onDone();
      }}
    />
  );
}

function describe(layer: Layer): string {
  switch (layer.type) {
    case 'text':
      return 'Text';
    case 'image':
      return 'Picture';
    case 'shape':
      return 'Shape';
    case 'svg':
      return 'Graphic';
    case 'qr':
      return 'QR code';
    case 'barcode':
      return 'Barcode';
    case 'group':
      return 'Group';
    default:
      return 'Field';
  }
}
